import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { assign, cloneDeep, isEmpty, max, padStart } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import {
    CenterMoveProps,
    CenterMoveReq,
    GoodsMoveProps,
    InventoryGoodsDetail,
    LocationArea,
    LocationDetail,
    LocationPasswordReq,
} from '../../types/inventory'
import { useFormik } from 'formik'
import { number, object, string } from 'yup'
import { InputNumber } from 'primereact/inputnumber'
import CenterDropdown from '../common/CenterDropdown'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const GoodsMove = (props: CenterMoveProps) => {
    const { $toast, $http, $alert } = useContext(CommonContext)
    const { t } = useTranslation()
    const [goods, setGoods] = useState<InventoryGoodsDetail>({
        rowNum: 0,
        lctSeq: '',
        goodsId: '',
        goodsNm: '',
        centerId: '',
        centerNm: '',
        invnSeq: 0,
        goodsQty: 0,
        pckGoodsQty: 0,
        avlbGoodsQty: 0,
        gdslsUnitCd: '',
        specTxt: '',
        shlfYmd: '',
        shlfYmdFm: '',
        shlfRemainDay: 0,
        strgDay: 0,
        prchUnitAmt: 0,
        imageUri: '',
        shlfYn: '',
        lotNo: '',
        sysCd: '',
    })

    useMountEffect(() => {
        if (!props.data) return
        setGoods(props.data)
        formik.validateField('goodsQty')
    })
    const initialValues: CenterMoveReq = {
        goodsQty: undefined,
        arvCenterId: '',
    }
    const formik = useFormik({
        initialValues,
        validationSchema: object({
            arvCenterId: string()
                .required(t(tokens.common.center_selection_is_required))
                .notOneOf([goods.centerId], `${t(tokens.common.cannot_move_to_the_same_center)} ` + goods.centerNm),
            goodsQty: number()
                .required(t(tokens.common.quantity_to_be_moved_must_be_entered))
                .min(1, t(tokens.common.please_enter_the_minimum_move_quantity_above))
                .max(
                    goods.avlbGoodsQty,
                    `${t(tokens.common.entered_movement_quantity_is_the_available_movement_quantity)}:` +
                        goods.avlbGoodsQty +
                        `${t(tokens.common.there_are_more)}`,
                ),
        }),
        onSubmit: async (param: CenterMoveReq) => {
            if (isEmpty(goods) || isEmpty(goods.lctSeq)) {
                $toast({ title: t(tokens.common.accept), text: t(tokens.common.no_items_to_transfer_to_center), icon: 'warning' })
                return
            }
            const isConfirm = await $alert({
                icon: 'info',
                title: t(tokens.common.move_center),
                text: goods.goodsNm + ' ${t(tokens.common.quantity_of_goods)}' + param.goodsQty + `${t(tokens.common.count_move_center)}`,
                isConfirm: true,
            })
            if (!isConfirm) return

            const data: any = {
                goodsQty: param.goodsQty,
                arvCenterId: param.arvCenterId,
                lctSeq: goods.lctSeq,
                goodsId: goods.goodsId,
                invnSeq: goods.invnSeq,
            }

            console.log('onSubmit', data)

            await $http()
                .put('/inventory/center-move', data)
                .then((result: any) => {
                    if (!result) return // null pass
                    if (result.succCd !== 'S') {
                        $toast({
                            title: t(tokens.common.fail),
                            icon: 'failure',
                        })
                        return
                    }
                    $toast({ title: t(tokens.common.success), text: t(tokens.common.center_transfer_completed), icon: 'success' })
                    props.onHide(true)
                })
        },
    })

    return (
        <>
            {goods && (
                <div className="grid">
                    <div className="col-12 flex font-bold mt-2">
                        <label className="text-color-blue text-lg required pl-2">{t(tokens.inventory.move_qty)}</label>
                    </div>
                    <div className="col-12 flex pt-0">
                        <InputNumber
                            id="goodsQty"
                            min={0}
                            placeholder={t(tokens.common.please_enter_the_quantity_to_move)}
                            inputStyle={{ paddingLeft: '1rem' }}
                            className="w-full"
                            value={formik.values.goodsQty}
                            onChange={(e) => formik.setFieldValue('goodsQty', e.value)}
                            locale="ko-KR"
                        />
                    </div>
                    <div className="col-12 flex pl-2 pt-0">
                        {formik.errors.goodsQty ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.goodsQty}
                            </span>
                        ) : null}
                    </div>
                    <div className="col-12 flex font-bold mt-2 pt-0">
                        <label className="text-color-blue text-lg required pl-2">{t(tokens.common.choose_center)}</label>
                    </div>
                    <div className="col-12 flex pt-0">
                        <CenterDropdown
                            id="arvCenterId"
                            placeholder={t(tokens.common.choose_center)}
                            type="single"
                            // onChange={formik.handleChange}
                            onChange={(value) => formik.setFieldValue('arvCenterId', value)}
                            width="100%"
                        />
                    </div>
                    <div className="col-12 flex pl-2 pt-0">
                        {formik.errors.arvCenterId ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.arvCenterId}
                            </span>
                        ) : null}
                    </div>
                    <form className="col-12 flex" onSubmit={formik.handleSubmit}>
                        <div className="col-6 flex pl-0">
                            <Button
                                className="w-full p-button-secondary"
                                label={t(tokens.common.close)}
                                size="large"
                                onClick={() => props.onHide(false)}
                            />
                        </div>
                        <div className="col-6 flex pr-0">
                            <Button className="w-full p-button-success" label={t(tokens.common.complete)} size="large" type="submit" />
                        </div>
                    </form>
                </div>
            )}
        </>
    )
}

export default GoodsMove
